import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  TextField,
} from "@mui/material";

import { useLazyQuery } from "@apollo/client";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import DownloadIcon from "@mui/icons-material/Download";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StarIcon from "@mui/icons-material/Star";
import GroupsIcon from "@mui/icons-material/Groups";
import BadgeIcon from "@mui/icons-material/Badge";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";

import moment from "moment";

import InsuranceTable from "./InsuranceTable.component";
import CsiTable from "./CsiTable.component";
import ServiceAppointmentsTable from "./ServiceAppointmentsTable.component";
import KnowledgeHubTable from "./KnowledgeHubTable.component";
import ReviewsTable from "./ReviewsTable.component";
import ReferralsTable from "./ReferralsTable.component";

import {
  BORDER_COLOR,
  TEXT_MUTED,
  TEXT_DARK,
  EmptyReportIcon,
} from "./Shared.component";

import Theme from "../utils/Theme";

import { useAuth } from "../context/Auth.context";
import { useAppContext } from "../context/App.context";
import { FETCH_DEALERSHIP_EMPLOYEES } from "../graphql/query/query";
import BreadcrumbsComponent from "../component/base/Breadcrumbs.component";

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const DEFAULT_EMPLOYEE = "ALL";

// FIXED: send the RAW browser offset (minutes behind UTC: IST = -330,
// US Eastern = +300). This is the same convention used by the customer
// list, the Manager page and getDealershipUsers. Previously this was
// multiplied by -1, which put the end-of-day boundary in the wrong place.
const TIMEZONE = new Date().getTimezoneOffset();

// NEW: breadcrumb config (title + "Home • Reports")
const breadcrumb = {
  title: "Reports",
  links: [
    { path: "/", name: "Home" },
    { path: null, name: "Reports" },
  ],
};

// -----------------------------------------------------------------------------
// Default dates
// -----------------------------------------------------------------------------

const getDefaultDateRange = () => ({
  startDate: moment().startOf("month").toDate(),
  endDate: moment().toDate(),
});

// -----------------------------------------------------------------------------
// Report options
// -----------------------------------------------------------------------------

const reportOptions = [
  {
    key: "insuranceVerified",
    label: "Insurance Verified",
    icon: VerifiedUserIcon,
    Component: InsuranceTable,
  },
  { key: "csi", label: "CSI", icon: ThumbUpIcon, Component: CsiTable },
  {
    key: "serviceAppointments",
    label: "Service Appointments",
    icon: EventAvailableIcon,
    Component: ServiceAppointmentsTable,
  },
  {
    key: "appDownloads",
    label: "App Downloads",
    icon: DownloadIcon,
    disabled: true,
  },
  {
    key: "preQuals",
    label: "PreQuals",
    icon: AssignmentTurnedInIcon,
    disabled: true,
  },
  {
    key: "knowledgeHub",
    label: "Knowledge Hub",
    icon: MenuBookIcon,
    Component: KnowledgeHubTable,
  },
  {
    key: "reviews",
    label: "Reviews",
    icon: StarIcon,
    Component: ReviewsTable,
  },
  {
    key: "referrals",
    label: "Referrals",
    icon: GroupsIcon,
    Component: ReferralsTable,
  },
  {
    key: "idVerification",
    label: "ID Verification",
    icon: BadgeIcon,
    disabled: true,
  },
  {
    key: "quotes",
    label: "Quotes",
    icon: RequestQuoteIcon,
    disabled: true,
  },
];

const sortedReportOptions = [...reportOptions].sort((a, b) => {
  if (!!a.disabled === !!b.disabled) {
    return 0;
  }
  return a.disabled ? 1 : -1;
});

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const ReportsPage = () => {
  const auth = useAuth();
  const { dealershipId: currentDealershipId } = useAppContext();

  // Dealership
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealershipId, setSelectedDealershipId] = useState("");

  // Employees
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] =
    useState(DEFAULT_EMPLOYEE);

  // Dates
  const defaultDates = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);

  // Report
  const [selectedReport, setSelectedReport] = useState(null);

  // NEW: bumping this remounts the active report table, which re-runs its API
  const [refreshKey, setRefreshKey] = useState(0);

  // Export / CSV
  const printRef = useRef(null);

  // Employee API
  const [
    fetchDealershipEmployees,
    { data: employeeData, error: employeesError },
  ] = useLazyQuery(FETCH_DEALERSHIP_EMPLOYEES, {
    fetchPolicy: "network-only",
  });

  // ---------------------------------------------------------------------------
  // Get dealerships from auth
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const associatedDealerships = auth.user?.associateDetails || [];

    const activeDealerships = associatedDealerships.filter(
      (dealer) =>
        dealer?.dealership_id &&
        dealer?.is_active !== false &&
        dealer?.user_active !== false
    );

    setDealerships(activeDealerships);

    if (activeDealerships.length === 0) {
      setSelectedDealershipId("");
      setEmployees([]);
      setSelectedEmployeeId(DEFAULT_EMPLOYEE);
      return;
    }

    const selectedDealerStillExists = activeDealerships.some(
      (dealer) =>
        Number(dealer.dealership_id) === Number(selectedDealershipId)
    );

    if (!selectedDealerStillExists) {
      const preferredDealership =
        activeDealerships.find(
          (dealer) =>
            Number(dealer.dealership_id) === Number(currentDealershipId)
        ) ||
        activeDealerships.find(
          (dealer) =>
            Number(dealer.dealership_id) === Number(auth.user?.dealership_id)
        ) ||
        activeDealerships[0];

      setSelectedDealershipId(
        preferredDealership ? Number(preferredDealership.dealership_id) : ""
      );
    }
  }, [
    auth.user?.associateDetails,
    auth.user?.dealership_id,
    selectedDealershipId,
    currentDealershipId,
  ]);

  // ---------------------------------------------------------------------------
  // Fetch employees when dealership changes
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!selectedDealershipId) {
      setEmployees([]);
      setSelectedEmployeeId(DEFAULT_EMPLOYEE);
      return;
    }

    setSelectedEmployeeId(DEFAULT_EMPLOYEE);
    setEmployees([]);

    fetchDealershipEmployees({
      variables: {
        employeeListInput: {
          dealership_id: Number(selectedDealershipId),
        },
      },
    });
  }, [selectedDealershipId, fetchDealershipEmployees]);

  // ---------------------------------------------------------------------------
  // Set employees from API
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const apiEmployees =
      employeeData?.fetchDealershipEmployees?.employees || [];

    const activeEmployees = apiEmployees.filter(
      (employee) => employee?.employee_user_id && employee?.is_active !== false
    );

    setEmployees(activeEmployees);
  }, [employeeData]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleOrganizationChange = (event) => {
    const dealershipId = event.target.value;

    setSelectedDealershipId(dealershipId === "" ? "" : Number(dealershipId));
    setSelectedEmployeeId(DEFAULT_EMPLOYEE);
    setEmployees([]);
  };

  const handleEmployeeChange = (event) => {
    setSelectedEmployeeId(event.target.value);
  };

  const handleStartDateChange = (newDate) => {
    if (!newDate) {
      setStartDate(null);
      return;
    }

    setStartDate(newDate);

    // Start date cannot be after end date.
    if (endDate && moment(newDate).isAfter(moment(endDate), "day")) {
      setEndDate(newDate);
    }
  };

  const handleEndDateChange = (newDate) => {
    if (!newDate) {
      setEndDate(null);
      return;
    }

    // End date cannot be before start date.
    if (startDate && moment(newDate).isBefore(moment(startDate), "day")) {
      setEndDate(startDate);
      return;
    }

    setEndDate(newDate);
  };

  const handleReset = () => {
    const defaults = getDefaultDateRange();

    setSelectedEmployeeId(DEFAULT_EMPLOYEE);
    setStartDate(defaults.startDate);
    setEndDate(defaults.endDate);
    setSelectedReport(null);

    // Prefer the store currently in context over the first in the list.
    if (dealerships.length > 0) {
      const preferredDealership =
        dealerships.find(
          (dealer) =>
            Number(dealer.dealership_id) === Number(currentDealershipId)
        ) ||
        dealerships.find(
          (dealer) =>
            Number(dealer.dealership_id) === Number(auth.user?.dealership_id)
        ) ||
        dealerships[0];

      setSelectedDealershipId(
        preferredDealership ? Number(preferredDealership.dealership_id) : ""
      );
    } else {
      setSelectedDealershipId("");
    }

    setEmployees([]);
  };

  // NEW: re-trigger the API of the currently selected report only
  const handleRefresh = () => {
    if (!selectedReport) {
      return;
    }
    setRefreshKey((prev) => prev + 1);
  };

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const activeReport = reportOptions.find(
    (report) => report.key === selectedReport
  );

  const ActiveReportTable = activeReport?.Component || null;

  const employeeUserId =
    selectedEmployeeId === DEFAULT_EMPLOYEE
      ? undefined
      : Number(selectedEmployeeId);

  const isEmployeeOnly = selectedEmployeeId !== DEFAULT_EMPLOYEE;

  const dealershipId = selectedDealershipId
    ? Number(selectedDealershipId)
    : undefined;

  const formattedStartDate = startDate
    ? moment(startDate).format("YYYY-MM-DD")
    : null;

  const formattedEndDate = endDate
    ? moment(endDate).format("YYYY-MM-DD")
    : null;

  const tableProps = {
    dealershipId,
    employeeUserId,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    dateRange: {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    },
    isEmployeeOnly,
    timezone: TIMEZONE,
  };

  const selectedDealership = dealerships.find(
    (dealer) => Number(dealer.dealership_id) === Number(selectedDealershipId)
  );

  // ---------------------------------------------------------------------------
  // Export / CSV
  // ---------------------------------------------------------------------------

  const exportDocumentTitle = useMemo(() => {
    const reportLabel = activeReport?.label?.replace(/\s+/g, "_") || "Report";

    const rangeSuffix =
      formattedStartDate && formattedEndDate
        ? `_${formattedStartDate}_to_${formattedEndDate}`
        : `_${moment().format("YYYY-MM-DD")}`;

    return `${reportLabel}${rangeSuffix}`;
  }, [activeReport?.label, formattedStartDate, formattedEndDate]);

  const handleExport = () => {
    if (!selectedReport || !printRef.current) {
      return;
    }

    const tables = printRef.current.querySelectorAll("table");

    if (!tables.length) {
      console.error("No table found to export as CSV.");
      return;
    }

    const csvRows = [];

    tables.forEach((table) => {
      table.querySelectorAll("tr").forEach((row) => {
        const cells = row.querySelectorAll("th, td");

        const values = Array.from(cells).map((cell) => {
          let value = "";

          const cellTitle = cell.getAttribute("title");

          if (cellTitle) {
            value = cellTitle;
          } else {
            const titledElements = cell.querySelectorAll("[title]");

            if (titledElements.length > 0) {
              value = Array.from(titledElements)
                .map((element) => element.getAttribute("title"))
                .filter(Boolean)
                .join(" | ");
            } else {
              value = cell.innerText.replace(/\s+/g, " ").trim();
            }
          }

          return `"${value.replace(/"/g, '""')}"`;
        });

        if (values.length) {
          csvRows.push(values.join(","));
        }
      });

      csvRows.push("");
    });

    const csvContent = "\uFEFF" + csvRows.join("\r\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${exportDocumentTitle}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------

  const datePickerTextFieldSx = {
    "& .MuiInputBase-root": {
      height: "40px",
      fontSize: "13px",
      borderRadius: "6px",
    },
    "& .MuiInputBase-input": {
      padding: "9px 12px",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "18px",
    },
  };

  const labelSx = {
    fontSize: "12px",
    fontWeight: 700,
    color: TEXT_DARK,
    mb: 0.5,
  };

  const actionButtonSx = {
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 600,
    borderColor: BORDER_COLOR,
    color: Theme.palette.primary.main,
    borderRadius: "6px",
    px: 2,
    py: 0.6,
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        width: "100%",
        backgroundColor: "#fff",
      }}
    >
      {/* Header: same structure as ManageusersTable (no page padding) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <BreadcrumbsComponent {...breadcrumb} />
      </Box>

      {/* Content: left padding lines everything up with the breadcrumb text */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 1,
          pl: "20px",
          pr: "29px",
        }}
      >
        {/* <Typography sx={{ fontSize: "13px", color: TEXT_MUTED, mb: 1 }}>
          Select your filters and choose a report to view performance data and
          trends.
        </Typography> */}

        {/* Filters */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            border: `1px solid ${BORDER_COLOR}`,
            borderRadius: "8px",
            p: "8px 18px 14px",
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          {/* Organization */}
          <Box sx={{ flex: 1, minWidth: "200px" }}>
            <Typography sx={labelSx}>Organization</Typography>

            <FormControl fullWidth size="small">
              <Select
                value={selectedDealershipId}
                onChange={handleOrganizationChange}
                displayEmpty
                disabled={dealerships.length === 0}
                sx={{ fontSize: "13px", borderRadius: "6px" }}
                renderValue={(value) => {
                  if (!value) {
                    return "No organization available";
                  }
                  return (
                    selectedDealership?.dealership_name || "Select Organization"
                  );
                }}
              >
                {dealerships.map((dealer) => (
                  <MenuItem
                    key={dealer.dealership_id}
                    value={Number(dealer.dealership_id)}
                  >
                    {dealer.dealership_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Employee */}
          <Box sx={{ flex: 1, minWidth: "200px" }}>
            <Typography sx={labelSx}>Employee (Optional)</Typography>

            <FormControl fullWidth size="small">
              <Select
                value={selectedEmployeeId}
                onChange={handleEmployeeChange}
                disabled={!selectedDealershipId || employees.length === 0}
                sx={{ fontSize: "13px", borderRadius: "6px" }}
                displayEmpty
              >
                <MenuItem value={DEFAULT_EMPLOYEE}>All Employees</MenuItem>

                {employees.map((employee) => (
                  <MenuItem
                    key={employee.employee_user_id}
                    value={Number(employee.employee_user_id)}
                  >
                    {employee.first_name} {employee.last_name}
                    {employee.employee_code
                      ? ` (${employee.employee_code})`
                      : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Start Date */}
          <Box sx={{ flex: 1, minWidth: "175px" }}>
            <Typography sx={labelSx}>Start Date</Typography>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                maxDate={endDate || new Date()}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    sx={datePickerTextFieldSx}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>

          {/* End Date */}
          <Box sx={{ flex: 1, minWidth: "175px" }}>
            <Typography sx={labelSx}>End Date</Typography>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                minDate={startDate || undefined}
                maxDate={new Date()}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    sx={datePickerTextFieldSx}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>

          {/* Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              alignItems: "flex-end",
              pt: 2.2,
            }}
          >
            <Button
              onClick={handleReset}
              startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
              variant="outlined"
              size="small"
              sx={actionButtonSx}
            >
              Reset
            </Button>

            {/* NEW: Refresh */}
            <Button
              onClick={handleRefresh}
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              variant="outlined"
              size="small"
              disabled={!selectedReport}
              sx={actionButtonSx}
            >
              Refresh
            </Button>

            <Button
              onClick={handleExport}
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
              variant="outlined"
              size="small"
              disabled={!selectedReport}
              sx={actionButtonSx}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Report Tabs */}
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            overflowX: "auto",
            pb: 1,
            mb: 1,
            borderBottom: `1px solid ${BORDER_COLOR}`,
          }}
        >
          {sortedReportOptions.map((report) => {
            const isSelected = selectedReport === report.key;
            const isDisabled = !!report.disabled;
            const Icon = report.icon;

            return (
              <Box
                key={report.key}
                onClick={
                  isDisabled ? undefined : () => setSelectedReport(report.key)
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  px: 2,
                  py: 1,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.45 : 1,
                  whiteSpace: "nowrap",
                  fontSize: "13px",
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? Theme.palette.primary.main : TEXT_MUTED,
                  backgroundColor: isSelected ? "#FFFFFF" : "transparent",
                  borderBottom: isSelected
                    ? `2px solid ${Theme.palette.primary.main}`
                    : "2px solid transparent",
                  transition: "all 0.15s ease",
                }}
              >
                <Icon
                  sx={{
                    fontSize: 18,
                    color: isSelected ? Theme.palette.primary.main : TEXT_MUTED,
                  }}
                />

                {report.label}
              </Box>
            );
          })}
        </Box>

        {/* Report (wrapped in printRef so Export can read the rendered table) */}
        {ActiveReportTable ? (
          <Box ref={printRef}>
            <Typography
              sx={{
                display: "none",
                "@media print": {
                  display: "block",
                  fontSize: "16px",
                  fontWeight: 700,
                  mb: 0.5,
                },
              }}
            >
              {selectedDealership?.dealership_name}
              {" — "}
              {activeReport?.label} Report
            </Typography>

            <Typography
              sx={{
                display: "none",
                "@media print": {
                  display: "block",
                  fontSize: "12px",
                  color: TEXT_MUTED,
                  mb: 1.5,
                },
              }}
            >
              Date Range: {formattedStartDate} - {formattedEndDate}
            </Typography>

            {/* key changes on Refresh -> table remounts -> its API fires again */}
            <ActiveReportTable
              key={`${selectedReport}-${refreshKey}`}
              {...tableProps}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              minHeight: "410px",
              border: `1px solid ${BORDER_COLOR}`,
              borderRadius: "8px",
              py: 6,
            }}
          >
            <Box
              sx={{
                width: 140,
                height: 140,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.5,
              }}
            >
              <EmptyReportIcon />
            </Box>

            <Typography
              sx={{ fontSize: "18px", fontWeight: 700, color: "#1B2027" }}
            >
              No report selected yet
            </Typography>

            <Typography sx={{ fontSize: "13px", color: TEXT_MUTED, mt: 0.5 }}>
              Choose a report above to view performance data and insights.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReportsPage;